import { safeExternalUrl } from '../../../utils/safeExternalUrl';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Table, Spin, message, Avatar, Modal, Tabs, Switch, Form, Tooltip, Row, Col, Input, InputNumber, Select, Popconfirm, Progress } from 'antd';
import { ArrowLeftOutlined, ShopOutlined, EditOutlined, LinkOutlined, InfoCircleOutlined, CheckCircleOutlined, StarFilled, FireOutlined, CopyOutlined, CheckOutlined, MinusOutlined, ReloadOutlined, CloudSyncOutlined, DollarOutlined, CrownOutlined, RocketOutlined, ThunderboltOutlined, WalletOutlined, GiftOutlined, StopOutlined, PercentageOutlined, PlusOutlined, HistoryOutlined, FileTextOutlined, ExclamationCircleOutlined, SafetyCertificateOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';
import { COUNTRIES } from '../../../utils/countries';

const { Title } = Typography;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  accentGreen: '#3FB950',
  hover: '#1f2a37',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  .ant-btn {
    background: ${COLORS.bgSecondary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
      background: ${COLORS.hover} !important;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    .ant-btn {
      width: 100%;
      justify-content: center;
    }
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table {
    background: transparent !important;
  }
  
  .ant-table-thead > tr > th {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table-tbody > tr > td {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-table-tbody > tr:hover > td {
    background: ${COLORS.hover} !important;
  }
  
  .ant-tabs {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-tabs-tab {
    color: ${COLORS.textSecondary} !important;
    
    &:hover {
      color: ${COLORS.accent} !important;
    }
  }
  
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${COLORS.accent} !important;
  }
  
  .ant-tabs-ink-bar {
    background: ${COLORS.accent} !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
    
    .ant-descriptions {
      .ant-descriptions-item-label,
      .ant-descriptions-item-content {
        padding: 8px 4px !important;
        font-size: 13px !important;
      }
    }
    
    .ant-table {
      font-size: 12px !important;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 4px !important;
    }
  }
`;

const CompanyDetails: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState<any>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [supportedNetworks, setSupportedNetworks] = useState<any[]>([]);
  const [supportedCurrencies, setSupportedCurrencies] = useState<any[]>([]);
  const [supportedContracts, setSupportedContracts] = useState<any[]>([]);
  const [loadingSupported, setLoadingSupported] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [publicKeyModalVisible, setPublicKeyModalVisible] = useState(false);
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  const [syncingToWatcher, setSyncingToWatcher] = useState(false);
  const [assigningPlan, setAssigningPlan] = useState(false);
  const [assigningGasWallet, setAssigningGasWallet] = useState(false);
  const [assigningInternalWallet, setAssigningInternalWallet] = useState(false);
  const [form] = Form.useForm();
  const [subscriptionForm] = Form.useForm();
  
  // Billing state
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [creditTransactions, setCreditTransactions] = useState<any[]>([]);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [internalWallet, setInternalWallet] = useState<any>(null);
  const [walletTransactions, setWalletTransactions] = useState<any[]>([]);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [resourceUsage, setResourceUsage] = useState<any[]>([]);
  const [loadingResourceUsage, setLoadingResourceUsage] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  
  // Modal state
  const [addCreditModalVisible, setAddCreditModalVisible] = useState(false);
  const [addingCredit, setAddingCredit] = useState(false);
  const [pricingModalVisible, setPricingModalVisible] = useState(false);
  const [updatingPricing, setUpdatingPricing] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [creditForm] = Form.useForm();
  const [pricingForm] = Form.useForm();

  // KYC state
  const [kycData, setKycData] = useState<any>(null);
  const [loadingKyc, setLoadingKyc] = useState(false);
  const [kycModalVisible, setKycModalVisible] = useState(false);
  const [updatingKyc, setUpdatingKyc] = useState(false);
  const [kycForm] = Form.useForm();

  useEffect(() => {
    fetchCompanyDetails();
    fetchSubscription();
    fetchCredits();
    fetchInternalWallet();
    fetchResourceUsage();
    fetchInvoices();
    fetchCompanyKyc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchSupportedResources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchSubscription = async () => {
    if (!companyId) return;
    
    try {
      setLoadingSubscription(true);
      const endpoint = API_ENDPOINTS.ADMIN_GET_MERCHANT_SUBSCRIPTION.replace('{companyId}', companyId);
      const response = await apiClient.get(endpoint);
      
      if (response.data.status === 'success') {
        setSubscription(response.data.data);
      }
    } catch (error: any) {
      // Subscription may not exist for this merchant, that's okay
      console.log('No subscription found for this merchant');
      setSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_ALL_SUBSCRIPTION_PLANS);
      if (response.data.status === 'success') {
        setSubscriptionPlans(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching subscription plans:', error);
    }
  };

  const fetchCredits = async () => {
    if (!companyId) return;
    try {
      setLoadingCredits(true);
      const endpoint = API_ENDPOINTS.ADMIN_GET_MERCHANT_CREDITS.replace('{companyId}', companyId);
      const response = await apiClient.get(endpoint);
      if (response.data.status === 'success') {
        setCreditBalance(response.data.data?.balance || 0);
        setCreditTransactions(response.data.data?.transactions || []);
      }
    } catch (error: any) {
      console.log('No credits found for this merchant');
      setCreditBalance(0);
      setCreditTransactions([]);
    } finally {
      setLoadingCredits(false);
    }
  };

  const fetchInternalWallet = async () => {
    if (!companyId) return;
    try {
      setLoadingWallet(true);
      const endpoint = API_ENDPOINTS.ADMIN_GET_MERCHANT_INTERNAL_WALLET.replace('{companyId}', companyId);
      const response = await apiClient.get(endpoint);
      if (response.data.status === 'success') {
        setInternalWallet(response.data.data?.wallet || response.data.data);
        setWalletTransactions(response.data.data?.transactions || []);
      }
    } catch (error: any) {
      console.log('No internal wallet found for this merchant');
      setInternalWallet(null);
      setWalletTransactions([]);
    } finally {
      setLoadingWallet(false);
    }
  };

  const fetchResourceUsage = async () => {
    if (!companyId) return;
    try {
      setLoadingResourceUsage(true);
      const endpoint = API_ENDPOINTS.ADMIN_GET_MERCHANT_RESOURCE_USAGE?.replace('{companyId}', companyId) || 
                       `/api/Administrator/merchants/${companyId}/resource-usage`;
      const response = await apiClient.get(endpoint);
      if (response.data.status === 'success') {
        setResourceUsage(response.data.data || []);
      }
    } catch (error: any) {
      console.log('No resource usage found for this merchant');
      setResourceUsage([]);
    } finally {
      setLoadingResourceUsage(false);
    }
  };

  const fetchInvoices = async () => {
    if (!companyId) return;
    try {
      setLoadingInvoices(true);
      const endpoint = API_ENDPOINTS.ADMIN_GET_MERCHANT_INVOICES?.replace('{merchantId}', companyId) ||
                       `/api/Administrator/merchants/${companyId}/invoices`;
      const response = await apiClient.get(endpoint, { params: { pageId: 1, perPage: 5 } });
      if (response.data.status === 'success') {
        setInvoices(response.data.data?.itemsList || response.data.data || []);
      }
    } catch (error: any) {
      console.log('No invoices found for this merchant');
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleAddCredit = async () => {
    try {
      const values = await creditForm.validateFields();
      setAddingCredit(true);

      const endpoint = API_ENDPOINTS.ADMIN_ADD_MERCHANT_CREDITS.replace('{companyId}', companyId as string);
      await apiClient.post(endpoint, {
        amount: values.amount,
        creditType: values.creditType || 'ADJUSTMENT',
        description: values.description,
        expiresAt: values.expiresAt || null,
      });

      message.success('Credit added successfully');
      setAddCreditModalVisible(false);
      creditForm.resetFields();
      fetchCredits();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to add credit');
    } finally {
      setAddingCredit(false);
    }
  };

  const handleUpdatePricing = async () => {
    try {
      const values = await pricingForm.validateFields();
      setUpdatingPricing(true);

      const endpoint = `/api/Administrator/merchants/${companyId}/subscription/pricing`;
      
      // Build payload with pricing and custom limits
      const payload: any = {
        customMonthlyPrice: values.customMonthlyPrice || null,
        discountType: values.discountType || null,
        discountValue: values.discountValue || null,
        pricingNotes: values.pricingNotes,
      };

      // Include custom limits if subscription is CUSTOM plan
      if (subscription?.subscriptionPlan?.name?.toUpperCase() === 'CUSTOM') {
        payload.customWalletLimit = values.customWalletLimit ?? null;
        payload.customStaffLimit = values.customStaffLimit ?? null;
        payload.customBlockchainLimit = values.customBlockchainLimit ?? null;
        payload.customCurrencyLimit = values.customCurrencyLimit ?? null;
        payload.customContractLimit = values.customContractLimit ?? null;
        payload.customNativeTokenLimit = values.customNativeTokenLimit ?? null;
        payload.customSupportsEvm = values.customSupportsEvm ?? null;
        payload.customSupportsBitcoin = values.customSupportsBitcoin ?? null;
        payload.customSupportsSolana = values.customSupportsSolana ?? null;
        payload.customSupportsTron = values.customSupportsTron ?? null;
        payload.customSupportsCustomChains = values.customSupportsCustomChains ?? null;
      }

      await apiClient.put(endpoint, payload);

      message.success('Settings updated successfully');
      setPricingModalVisible(false);
      pricingForm.resetFields();
      fetchSubscription();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setUpdatingPricing(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      const endpoint = API_ENDPOINTS.ADMIN_CANCEL_MERCHANT_SUBSCRIPTION?.replace('{companyId}', companyId!) ||
                       `/api/Administrator/merchants/${companyId}/subscription/cancel`;
      await apiClient.post(endpoint);
      message.success('Subscription cancelled successfully');
      fetchSubscription();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleOpenPricingModal = () => {
    if (subscription) {
      const formValues: any = {
        customMonthlyPrice: subscription.customMonthlyPrice,
        discountType: subscription.discountType,
        discountValue: subscription.discountValue,
        pricingNotes: subscription.pricingNotes,
      };

      // Include custom limits if they exist
      if (subscription.subscriptionPlan?.name?.toUpperCase() === 'CUSTOM') {
        formValues.customWalletLimit = subscription.customWalletLimit;
        formValues.customStaffLimit = subscription.customStaffLimit;
        formValues.customBlockchainLimit = subscription.customBlockchainLimit;
        formValues.customCurrencyLimit = subscription.customCurrencyLimit;
        formValues.customContractLimit = subscription.customContractLimit;
        formValues.customNativeTokenLimit = subscription.customNativeTokenLimit;
        formValues.customSupportsEvm = subscription.customSupportsEvm;
        formValues.customSupportsBitcoin = subscription.customSupportsBitcoin;
        formValues.customSupportsSolana = subscription.customSupportsSolana;
        formValues.customSupportsTron = subscription.customSupportsTron;
        formValues.customSupportsCustomChains = subscription.customSupportsCustomChains;
      }

      pricingForm.setFieldsValue(formValues);
    }
    setPricingModalVisible(true);
  };

  const handleOpenSubscriptionModal = () => {
    fetchSubscriptionPlans();
    if (subscription) {
      subscriptionForm.setFieldsValue({
        subscriptionPlanId: subscription.subscriptionPlanId,
      });
      setSelectedPlanId(subscription.subscriptionPlanId);
    } else {
      subscriptionForm.resetFields();
      setSelectedPlanId(null);
    }
    setSubscriptionModalVisible(true);
  };

  const handleAssignSubscription = async () => {
    try {
      const values = await subscriptionForm.validateFields();
      setAssigningPlan(true);

      if (subscription) {
        // Update existing subscription
        const endpoint = API_ENDPOINTS.ADMIN_UPDATE_MERCHANT_SUBSCRIPTION.replace('{companyId}', companyId!);
        await apiClient.put(endpoint, {
          subscriptionPlanId: values.subscriptionPlanId,
        });
        message.success('Subscription updated successfully');
      } else {
        // Create new subscription
        const endpoint = API_ENDPOINTS.ADMIN_CREATE_MERCHANT_SUBSCRIPTION.replace('{companyId}', companyId!);
        await apiClient.post(endpoint, {
          subscriptionPlanId: values.subscriptionPlanId,
        });
        message.success('Subscription assigned successfully');
      }

      setSubscriptionModalVisible(false);
      fetchSubscription();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to assign subscription');
    } finally {
      setAssigningPlan(false);
    }
  };

  const fetchCompanyDetails = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const endpoint = API_ENDPOINTS.ADMIN_GET_COMPANY_DETAILS.replace('{companyId}', companyId);
      const response = await apiClient.get(endpoint);
      
      if (response.data.status === 'success') {
        setCompanyData(response.data.data);
      } else {
        message.error('Failed to load company details');
      }
    } catch (error: any) {
      console.error('Error fetching company details:', error);
      message.error(error.response?.data?.message || 'Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportedResources = async () => {
    if (!companyId) return;
    
    try {
      setLoadingSupported(true);
      
      // Fetch supported blockchain networks
      try {
        const networksResponse = await apiClient.get(
          API_ENDPOINTS.ADMIN_GET_MERCHANT_SUPPORTED_BLOCKCHAIN_NETWORKS.replace('{companyId}', companyId),
          { params: { pageId: 1, perPage: 100 } }
        );
        if (networksResponse.data?.status === 'success' && networksResponse.data?.data) {
          setSupportedNetworks(networksResponse.data.data.itemsList || []);
        }
      } catch (error) {
        console.error('Error fetching supported networks:', error);
      }

      // Fetch supported currencies
      try {
        const currenciesResponse = await apiClient.get(
          API_ENDPOINTS.ADMIN_GET_MERCHANT_SUPPORTED_CURRENCIES.replace('{companyId}', companyId),
          { params: { pageId: 1, perPage: 100 } }
        );
        if (currenciesResponse.data?.status === 'success' && currenciesResponse.data?.data) {
          setSupportedCurrencies(currenciesResponse.data.data.itemsList || []);
        }
      } catch (error) {
        console.error('Error fetching supported currencies:', error);
      }

      // Fetch supported contract addresses
      try {
        const contractsResponse = await apiClient.get(
          API_ENDPOINTS.ADMIN_GET_MERCHANT_SUPPORTED_CONTRACT_ADDRESSES.replace('{companyId}', companyId),
          { params: { pageId: 1, perPage: 100 } }
        );
        if (contractsResponse.data?.status === 'success' && contractsResponse.data?.data) {
          setSupportedContracts(contractsResponse.data.data.itemsList || []);
        }
      } catch (error) {
        console.error('Error fetching supported contracts:', error);
      }
    } catch (error: any) {
      console.error('Error fetching supported resources:', error);
    } finally {
      setLoadingSupported(false);
    }
  };

  const fetchCompanyKyc = async () => {
    if (!companyId) return;
    try {
      setLoadingKyc(true);
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_COMPANY_KYC.replace('{companyId}', companyId)
      );
      if (response.data?.status === 'success') {
        setKycData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching KYC data:', error);
    } finally {
      setLoadingKyc(false);
    }
  };

  const handleUpdateKyc = async () => {
    if (!companyId) return;
    try {
      const values = await kycForm.validateFields();
      setUpdatingKyc(true);
      await apiClient.post(
        API_ENDPOINTS.ADMIN_UPDATE_COMPANY_KYC.replace('{companyId}', companyId),
        {
          action: values.kycAction,
          rejectionReason: values.rejectionReason || undefined,
        }
      );
      message.success(
        `Business verification ${values.kycAction === 'APPROVE' ? 'approved' : 'rejected'} successfully`
      );
      setKycModalVisible(false);
      kycForm.resetFields();
      fetchCompanyKyc();
      fetchCompanyDetails();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update KYC status');
    } finally {
      setUpdatingKyc(false);
    }
  };

  const handleEdit = () => {
    if (!companyData) return;

    form.setFieldsValue({
      isDeleted: companyData.status === 'DELETED',
      isDisabled: companyData.merchantStatus === 'SUSPENDED',
      resetAsoRockAcknowledged: false,
      resetWatcherId: false,
    });
    setEditModalVisible(true);
  };

  const handleUpdateStatus = async () => {
    if (!companyId) return;

    try {
      const values = await form.validateFields();
      const payload: any = {};

      // Handle delete status
      if (values.isDeleted) {
        payload.status = 'DELETED';
      } else if (companyData.status === 'DELETED') {
        // If currently deleted and toggling off, reactivate
        payload.status = 'ACTIVE';
      }

      // Handle disable status
      if (values.isDisabled) {
        payload.merchantStatus = 'SUSPENDED';
      } else if (companyData.merchantStatus === 'SUSPENDED') {
        // If currently suspended and toggling off, activate
        payload.merchantStatus = 'ACTIVE';
      }

      // Handle Aso Rock acknowledgment reset
      if (values.resetAsoRockAcknowledged) {
        payload.asorockAcknowledged = false;
      }

      // Handle Watcher ID reset
      if (values.resetWatcherId) {
        payload.resetWatcherId = true;
      }

      await apiClient.post(
        `${API_ENDPOINTS.ADMIN_UPDATE_COMPANY_STATUS.replace('{companyId}', companyId)}`,
        payload
      );

      message.success('Company updated successfully');
      setEditModalVisible(false);
      fetchCompanyDetails(); // Refresh data
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update company');
    }
  };

  const handleActivateMerchant = async () => {
    if (!companyId) return;

    // Validate EVM custodial wallet exists
    if (!companyData?.custodialEVMAddress) {
      message.error('Merchant must add a custodial EVM wallet before activation.');
      return;
    }

    setActivating(true);
    try {
      await apiClient.post(
        `${API_ENDPOINTS.ADMIN_ACTIVATE_MERCHANT.replace('{companyId}', companyId)}`
      );

      message.success('Merchant activated successfully and gas wallet assigned');
      setActivateModalVisible(false);
      fetchCompanyDetails(); // Refresh data
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to activate merchant');
    } finally {
      setActivating(false);
    }
  };

  const handleAssignGasWallet = async () => {
    if (!companyId) return;

    // Validate merchant status is ACTIVE
    if (companyData?.merchantStatus !== 'ACTIVE') {
      message.error('Merchant must be ACTIVE to assign a gas wallet. Current status: ' + companyData?.merchantStatus);
      return;
    }

    setAssigningGasWallet(true);
    try {
      const endpoint = API_ENDPOINTS.ADMIN_ASSIGN_GAS_WALLET.replace('{companyId}', companyId);
      await apiClient.post(endpoint);

      message.success('Gas wallet assigned successfully');
      fetchCompanyDetails(); // Refresh data
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to assign gas wallet');
    } finally {
      setAssigningGasWallet(false);
    }
  };

  const handleAssignInternalWallet = async () => {
    if (!companyId) return;

    // Validate merchant status is ACTIVE
    if (companyData?.merchantStatus !== 'ACTIVE') {
      message.error('Merchant must be ACTIVE to assign an internal wallet. Current status: ' + companyData?.merchantStatus);
      return;
    }

    setAssigningInternalWallet(true);
    try {
      const endpoint = API_ENDPOINTS.ADMIN_ASSIGN_INTERNAL_WALLET.replace('{companyId}', companyId);
      await apiClient.post(endpoint);

      message.success('Internal wallet assigned successfully');
      fetchCompanyDetails(); // Refresh data
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to assign internal wallet');
    } finally {
      setAssigningInternalWallet(false);
    }
  };

  const handleSyncToWatcher = async () => {
    if (!companyId) return;
    
    setSyncingToWatcher(true);
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_RESYNC_COMPANY_TO_WATCHER.replace('{companyId}', companyId)
      );
      
      if (response.data?.status === 'success') {
        message.success('Company synced to OnchainWatcher successfully');
        fetchCompanyDetails(); // Refresh data
      } else {
        message.error(response.data?.message || 'Failed to sync to watcher');
      }
    } catch (error: any) {
      console.error('Error syncing company to watcher:', error);
      message.error(error.response?.data?.message || 'Failed to sync to watcher');
    } finally {
      setSyncingToWatcher(false);
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      ACTIVE: { color: '#10B981', text: 'Active' },
      PENDING: { color: '#F59E0B', text: 'Pending' },
      SUSPENDED: { color: '#EF4444', text: 'Suspended' },
      DELETED: { color: '#6B7280', text: 'Deleted' },
    };
    const config = statusConfig[status] || { color: '#6B7280', text: status };
    return (
      <Tag 
        color="transparent" 
        style={{ 
          color: config.color, 
          border: `1px solid ${config.color}40`,
          background: `${config.color}10`,
          borderRadius: '6px',
          padding: '0 8px'
        }}
      >
        {config.text}
      </Tag>
    );
  };

  const merchantColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
  ];

  const supportedNetworksColumns = [
    {
      title: 'Network Name',
      dataIndex: 'name',
      key: 'networkName',
      render: (name: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={record.iconUrl}
            alt="Network Icon"
            style={{ width: 20, height: 20, borderRadius: '50%' }}
          />
          <span>{name}</span>
        </div>
      ),
    },
    {
      title: 'Abbreviation',
      dataIndex: 'abbreviation',
      key: 'abbreviation',
    },
    {
      title: 'Blockchain Type',
      dataIndex: 'blockchainType',
      key: 'blockchainType',
    },
    {
      title: 'Status',
      dataIndex: 'isSupported',
      key: 'isSupported',
      render: (isSupported: boolean) => (
        <Tag color={isSupported ? 'green' : 'default'}>
          {isSupported ? 'Supported' : 'Not Supported'}
        </Tag>
      ),
    },
  ];

  const supportedCurrenciesColumns = [
    {
      title: 'Currency',
      key: 'currencyName',
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {record.iconUrl && (
            <Avatar src={record.iconUrl} size={36} />
          )}
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px' }}>{record.name}</div>
            <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
              {record.symbol}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Available On',
      key: 'blockchains',
      render: (_: any, record: any) => {
        const blockchains = record.blockchains || [];
        if (blockchains.length === 0) return <span style={{ color: COLORS.textSecondary }}>-</span>;
        
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
            {blockchains.slice(0, 5).map((chain: any) => (
              <Tooltip key={chain.id} title={chain.name || chain.abbreviation}>
                <Avatar 
                  src={chain.iconUrl} 
                  size={22}
                  style={{ border: `1px solid ${COLORS.border}` }}
                >
                  {!chain.iconUrl && (chain.abbreviation?.[0] || '?')}
                </Avatar>
              </Tooltip>
            ))}
            {blockchains.length > 5 && (
              <Tooltip title={blockchains.slice(5).map((c: any) => c.name).join(', ')}>
                <Tag style={{ marginLeft: 4, fontSize: '11px' }}>+{blockchains.length - 5}</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Contracts',
      key: 'contracts',
      width: 140,
      render: (_: any, record: any) => {
        const total = record.totalContracts || 0;
        const supported = record.supportedContracts || 0;
        const isFullySupported = total > 0 && supported === total;
        
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              {isFullySupported ? (
                <CheckOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
              ) : supported > 0 ? (
                <MinusOutlined style={{ color: COLORS.accent, fontSize: '12px' }} />
              ) : (
                <MinusOutlined style={{ color: COLORS.textSecondary, fontSize: '12px' }} />
              )}
              <span style={{ fontSize: '13px' }}>
                {supported} / {total}
              </span>
            </div>
            <div style={{ 
              height: '4px', 
              background: COLORS.border, 
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{ 
                height: '100%', 
                width: total > 0 ? `${(supported / total) * 100}%` : '0%',
                background: isFullySupported ? '#52c41a' : COLORS.accent,
                borderRadius: '2px',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'isSupported',
      width: 100,
      render: (_: any, record: any) => {
        const isSupported = record.isSupported || record.supportedContracts > 0;
        const isFullySupported = record.isFullySupported || (record.totalContracts > 0 && record.supportedContracts === record.totalContracts);
        
        if (isFullySupported) {
          return <Tag color="green">All Enabled</Tag>;
        } else if (isSupported) {
          return <Tag color="blue">Partial</Tag>;
        }
        return <Tag color="default">Disabled</Tag>;
      },
    },
  ];

  const supportedContractsColumns = [
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (address: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={record.currency?.iconUrl}
            alt="Currency Icon"
            style={{ width: 20, height: 20, borderRadius: '50%' }}
          />
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {address}
          </span>
        </div>
      ),
    },
    {
      title: 'Currency',
      dataIndex: ['currency', 'name'],
      key: 'currencyName',
    },
    {
      title: 'Symbol',
      dataIndex: ['currency', 'symbol'],
      key: 'symbol',
    },
    {
      title: 'Blockchain Network',
      dataIndex: ['blockchainNetwork', 'name'],
      key: 'blockchainNetwork',
    },
    {
      title: 'Status',
      dataIndex: 'isSupported',
      key: 'isSupported',
      render: (isSupported: boolean) => (
        <Tag color={isSupported ? 'green' : 'default'}>
          {isSupported ? 'Supported' : 'Not Supported'}
        </Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!companyData) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Company Not Found
          </Title>
        </HeaderContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/kivu/companies')}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Company Details
          </Title>
        </Space>
        <Space>
          {companyData?.merchantStatus === 'PENDING' && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setActivateModalVisible(true)}
              style={{ background: COLORS.accent, borderColor: COLORS.accent }}
            >
              Activate Merchant
            </Button>
          )}
          <Button
            icon={<CloudSyncOutlined />}
            onClick={handleSyncToWatcher}
            loading={syncingToWatcher}
          >
            Sync to Watcher
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit Status
          </Button>
        </Space>
      </HeaderContainer>

      <StyledCard
        title={
          <Space>
            <Avatar
              src={companyData.logoUrl}
              icon={<ShopOutlined />}
              size={64}
              style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
            />
            <div>
              <div style={{ color: COLORS.textPrimary, fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                {companyData.name}
                {companyData.isDefault && (
                  <Tag icon={<StarFilled />} color="gold">
                    Default Merchant
                  </Tag>
                )}
              </div>
              {companyData.tagline && (
                <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
                  {companyData.tagline}
                </div>
              )}
            </div>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="Company ID">{companyData.id}</Descriptions.Item>
          <Descriptions.Item label="Watcher ID">
            {companyData.watcherId ? (
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{companyData.watcherId}</span>
            ) : (
              <span style={{ color: COLORS.textSecondary, fontStyle: 'italic' }}>Not synced</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Company Name">{companyData.name}</Descriptions.Item>
          <Descriptions.Item label="Tagline">{companyData.tagline || '-'}</Descriptions.Item>
          <Descriptions.Item label="Email">{companyData.companyEmail}</Descriptions.Item>
          <Descriptions.Item label="Country">
            <Space>
              {companyData.countryCode && (
                <ReactCountryFlag
                  countryCode={companyData.countryCode}
                  svg
                  style={{
                    width: '1.5em',
                    height: '1.5em',
                    borderRadius: '2px'
                  }}
                />
              )}
              <span>{COUNTRIES.find(c => c.code === companyData.countryCode)?.name || companyData.countryCode || '-'}</span>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Status">{getStatusTag(companyData.status)}</Descriptions.Item>
          <Descriptions.Item label="Company Status">
            {companyData.merchantStatus ? getStatusTag(companyData.merchantStatus) : '-'}
          </Descriptions.Item>
          <Descriptions.Item 
            label={
              <Space>
                <span>Aso Rock Acknowledged</span>
                <Tooltip title="Indicates whether Aso Rock has synced this merchant company profile. When custodial wallet is updated and approved by admin, this is set to false so Aso Rock will re-sync the updated profile.">
                  <InfoCircleOutlined 
                    style={{ color: COLORS.textSecondary, cursor: 'help' }}
                  />
                </Tooltip>
              </Space>
            }
          >
            <Tag color={companyData.asorockAcknowledged ? 'green' : 'orange'}>
              {companyData.asorockAcknowledged ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          {companyData.logoUrl && (
            <Descriptions.Item label="Logo URL">
              <a href={safeExternalUrl(companyData.logoUrl)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                {companyData.logoUrl}
              </a>
            </Descriptions.Item>
          )}
          {companyData.websiteUrl && (
            <Descriptions.Item label="Website URL">
              <a href={safeExternalUrl(companyData.websiteUrl)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                {companyData.websiteUrl}
              </a>
            </Descriptions.Item>
          )}
          {companyData.webHookPostUrl && (
            <Descriptions.Item label="Webhook URL">
              <a href={safeExternalUrl(companyData.webHookPostUrl)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                {companyData.webHookPostUrl}
              </a>
            </Descriptions.Item>
          )}
          {companyData.webHookSecret && (
            <Descriptions.Item label="Webhook Secret">
              <span style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                {companyData.webHookSecret}
              </span>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Custodial EVM Address">
            {companyData.custodialEVMAddress ? (
              <span style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                {companyData.custodialEVMAddress}
              </span>
            ) : (
              <Tag color="orange">Not provided yet</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Custodial Bitcoin Address">
            {companyData.bitcoinCustodialWallet ? (
              <span style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                {companyData.bitcoinCustodialWallet}
              </span>
            ) : (
              <Tag color="orange">Not provided yet</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Custodial Solana Address">
            {companyData.solanaCustodialWallet ? (
              <span style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                {companyData.solanaCustodialWallet}
              </span>
            ) : (
              <Tag color="orange">Not provided yet</Tag>
            )}
          </Descriptions.Item>
          {companyData.publicKey && (
            <Descriptions.Item label="Public Key">
              <Button
                type="link"
                onClick={() => setPublicKeyModalVisible(true)}
                style={{ padding: 0, color: COLORS.accent }}
              >
                View Public Key
              </Button>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Created At">
            {companyData.createdAt ? new Date(companyData.createdAt).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {companyData.updatedAt ? new Date(companyData.updatedAt).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Gas Wallet">
            {companyData.gasWalletId ? (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Button
                    type="link"
                    icon={<FireOutlined />}
                    onClick={() => navigate(`/kivu/companies/${companyId}/gas-station`)}
                    style={{ padding: 0, color: COLORS.accent }}
                  >
                    View Gas Station
                  </Button>
                </Space>
                {companyData.gasWallet?.walletId && (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', color: COLORS.textSecondary }}>
                      Wallet ID: {companyData.gasWallet.walletId}
                    </span>
                    <Tooltip title="Copy Wallet ID">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(companyData.gasWallet.walletId);
                          message.success('Wallet ID copied to clipboard');
                        }}
                        style={{ padding: 0, color: COLORS.textSecondary }}
                      />
                    </Tooltip>
                  </Space>
                )}
              </Space>
            ) : (
              <Space direction="vertical" size="small">
                {companyData.merchantStatus === 'ACTIVE' ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAssignGasWallet}
                    loading={assigningGasWallet}
                    style={{ background: COLORS.accentGreen, borderColor: COLORS.accentGreen }}
                  >
                    Create Gas Wallet
                  </Button>
                ) : (
                  <span style={{ color: COLORS.textSecondary }}>
                    Pending Gas Wallet (Merchant must be ACTIVE)
                  </span>
                )}
              </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Internal Wallet">
            {companyData.internalWalletId ? (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Button
                    type="link"
                    icon={<WalletOutlined />}
                    onClick={() => navigate(`/kivu/companies/${companyId}/internal-wallet`)}
                    style={{ padding: 0, color: COLORS.accent }}
                  >
                    View Internal Wallet
                  </Button>
                </Space>
                {companyData.internalWallet?.walletId && (
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', color: COLORS.textSecondary }}>
                      Wallet ID: {companyData.internalWallet.walletId}
                    </span>
                    <Tooltip title="Copy Wallet ID">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(companyData.internalWallet.walletId);
                          message.success('Wallet ID copied to clipboard');
                        }}
                        style={{ padding: 0, color: COLORS.textSecondary }}
                      />
                    </Tooltip>
                  </Space>
                )}
              </Space>
            ) : (
              <Space direction="vertical" size="small">
                {companyData.merchantStatus === 'ACTIVE' ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAssignInternalWallet}
                    loading={assigningInternalWallet}
                    style={{ background: COLORS.accentGreen, borderColor: COLORS.accentGreen }}
                  >
                    Create Internal Wallet
                  </Button>
                ) : (
                  <span style={{ color: COLORS.textSecondary }}>
                    Pending Internal Wallet (Merchant must be ACTIVE)
                  </span>
                )}
              </Space>
            )}
          </Descriptions.Item>
          {companyData.walletCounts && (
            <Descriptions.Item label="Wallets">
              <Space direction="vertical" size="small">
                <div>
                  <strong>Total:</strong> {companyData.walletCounts.total || 0}
                </div>
                <div>
                  <strong>Assigned:</strong> {companyData.walletCounts.assigned || 0}
                </div>
                <div>
                  <strong>Unassigned:</strong> {companyData.walletCounts.unassigned || 0}
                </div>
                <Button
                  type="link"
                  icon={<LinkOutlined />}
                  onClick={() => navigate(`/kivu/wallets?merchantCompanyId=${companyId}`)}
                  style={{ padding: 0, color: COLORS.accent }}
                >
                  View All Wallets
                </Button>
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </StyledCard>

      {/* Subscription & Billing Section - Hidden for Default Merchant */}
      {!companyData.isDefault && (
      <StyledCard 
        title={
          <Space>
            <DollarOutlined />
            <span>Subscription & Billing</span>
          </Space>
        }
        extra={
          <Space wrap>
            {subscription && subscription.status === 'ACTIVE' && (
              <>
                <Button
                  icon={<PercentageOutlined />}
                  onClick={handleOpenPricingModal}
                  style={{ background: COLORS.bgSecondary, borderColor: COLORS.border, color: COLORS.textPrimary }}
                >
                  Custom Pricing
                </Button>
                <Popconfirm
                  title={
                    <div>
                      <div style={{ fontWeight: 600 }}>Cancel Subscription?</div>
                      <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: 4 }}>
                        This will cancel the subscription at the end of the current billing period.
                      </div>
                    </div>
                  }
                  onConfirm={handleCancelSubscription}
                  okText="Yes, Cancel"
                  cancelText="No"
                  okButtonProps={{ danger: true, loading: cancellingSubscription }}
                >
                  <Button
                    danger
                    icon={<StopOutlined />}
                    loading={cancellingSubscription}
                  >
                    Cancel
                  </Button>
                </Popconfirm>
              </>
            )}
            <Button
              type="primary"
              icon={subscription ? <EditOutlined /> : <CrownOutlined />}
              onClick={handleOpenSubscriptionModal}
              style={{ background: COLORS.accent, borderColor: COLORS.accent }}
            >
              {subscription ? 'Change Plan' : 'Assign Plan'}
            </Button>
          </Space>
        }
      >
        <Spin spinning={loadingSubscription}>
          {subscription ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
              {/* Current Plan Card */}
              <div style={{ 
                background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%)',
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${COLORS.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #58A6FF 0%, #1F6FEB 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white',
                  }}>
                    {subscription.subscriptionPlan?.name?.toUpperCase().includes('ENTERPRISE') ? <CrownOutlined /> :
                     subscription.subscriptionPlan?.name?.toUpperCase().includes('PRO') ? <ThunderboltOutlined /> :
                     <RocketOutlined />}
                  </div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: 600, color: COLORS.textPrimary }}>
                      {subscription.subscriptionPlan?.displayName || subscription.subscriptionPlan?.name || 'Unknown Plan'}
                    </div>
                    <Tag color={subscription.status === 'ACTIVE' ? 'green' : subscription.status === 'CANCELLED' ? 'red' : 'orange'}>
                      {subscription.status}
                    </Tag>
                  </div>
                </div>
                
                {/* Smart Pricing Display */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    <span style={{ fontSize: '32px', fontWeight: 700, color: COLORS.textPrimary }}>
                      ${parseFloat(subscription.effectivePrice || subscription.subscriptionPlan?.monthlyPrice || 0).toFixed(2)}
                    </span>
                    <span style={{ color: COLORS.textSecondary }}>/month</span>
                  </div>
                  
                  {/* Show discount breakdown if applicable */}
                  {(subscription.customMonthlyPrice || subscription.discountType) && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px 12px', 
                      background: 'rgba(16, 185, 129, 0.1)', 
                      borderRadius: '8px',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                    }}>
                      <div style={{ fontSize: '12px', color: '#10B981', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {subscription.customMonthlyPrice && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Custom Price:</span>
                            <span style={{ fontWeight: 600 }}>${parseFloat(subscription.customMonthlyPrice).toFixed(2)}</span>
                          </div>
                        )}
                        {subscription.discountType === 'PERCENTAGE' && subscription.discountValue && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Discount:</span>
                            <span style={{ fontWeight: 600 }}>{subscription.discountValue}% off</span>
                          </div>
                        )}
                        {subscription.discountType === 'FIXED_AMOUNT' && subscription.discountValue && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Discount:</span>
                            <span style={{ fontWeight: 600 }}>-${parseFloat(subscription.discountValue).toFixed(2)}</span>
                          </div>
                        )}
                        {subscription.subscriptionPlan?.monthlyPrice && parseFloat(subscription.effectivePrice) < parseFloat(subscription.subscriptionPlan.monthlyPrice) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px', borderTop: '1px dashed rgba(16, 185, 129, 0.3)' }}>
                            <span>Savings:</span>
                            <span style={{ fontWeight: 600 }}>
                              ${(parseFloat(subscription.subscriptionPlan.monthlyPrice) - parseFloat(subscription.effectivePrice)).toFixed(2)}/mo
                              ({Math.round((1 - parseFloat(subscription.effectivePrice) / parseFloat(subscription.subscriptionPlan.monthlyPrice)) * 100)}%)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {subscription.subscriptionPlan?.description && (
                  <p style={{ color: COLORS.textSecondary, fontSize: '14px', marginBottom: '16px' }}>
                    {subscription.subscriptionPlan.description}
                  </p>
                )}
                
                {/* Pricing Notes */}
                {subscription.pricingNotes && (
                  <div style={{ 
                    padding: '8px 12px', 
                    background: 'rgba(139, 92, 246, 0.1)', 
                    borderRadius: '8px',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    fontSize: '12px',
                    color: '#A78BFA',
                  }}>
                    <strong>Note:</strong> {subscription.pricingNotes}
                  </div>
                )}
              </div>

              {/* Plan Limits */}
              <div style={{ 
                background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%)',
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${COLORS.border}`,
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: COLORS.textSecondary, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Resource Limits
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {[
                    { label: 'Wallets', value: subscription.customWalletLimit ?? subscription.subscriptionPlan?.walletLimit, isCustom: subscription.customWalletLimit != null },
                    { label: 'Staff', value: subscription.customStaffLimit ?? subscription.subscriptionPlan?.staffLimit, isCustom: subscription.customStaffLimit != null },
                    { label: 'Blockchains', value: subscription.customBlockchainLimit ?? subscription.subscriptionPlan?.blockchainLimit, isCustom: subscription.customBlockchainLimit != null },
                    { label: 'Currencies', value: subscription.customCurrencyLimit ?? subscription.subscriptionPlan?.currencyLimit, isCustom: subscription.customCurrencyLimit != null },
                    { label: 'Contracts', value: subscription.customContractLimit ?? subscription.subscriptionPlan?.contractLimit, isCustom: subscription.customContractLimit != null },
                    { label: 'Native Tokens', value: subscription.customNativeTokenLimit ?? subscription.subscriptionPlan?.nativeTokenLimit, isCustom: subscription.customNativeTokenLimit != null },
                  ].map((item) => (
                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                      <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                        {item.label}
                        {item.isCustom && <Tag color="purple" style={{ marginLeft: 4, fontSize: '10px', padding: '0 4px' }}>Custom</Tag>}
                      </span>
                      <span style={{ 
                        color: item.value === -1 ? '#3FB950' : COLORS.textPrimary, 
                        fontWeight: 600,
                        fontSize: '13px',
                      }}>
                        {item.value === -1 ? 'Unlimited' : item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Supported Blockchains */}
              <div style={{ 
                background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%)',
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${COLORS.border}`,
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: COLORS.textSecondary, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Supported Blockchains
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {(subscription.customSupportsEvm ?? subscription.subscriptionPlan?.supportsEvm) && (
                    <Tag color="blue" style={{ padding: '4px 12px', borderRadius: '6px' }}>
                      EVM Chains {subscription.customSupportsEvm != null && '★'}
                    </Tag>
                  )}
                  {(subscription.customSupportsBitcoin ?? subscription.subscriptionPlan?.supportsBitcoin) && (
                    <Tag color="orange" style={{ padding: '4px 12px', borderRadius: '6px' }}>
                      Bitcoin {subscription.customSupportsBitcoin != null && '★'}
                    </Tag>
                  )}
                  {(subscription.customSupportsSolana ?? subscription.subscriptionPlan?.supportsSolana) && (
                    <Tag color="purple" style={{ padding: '4px 12px', borderRadius: '6px' }}>
                      Solana {subscription.customSupportsSolana != null && '★'}
                    </Tag>
                  )}
                  {(subscription.customSupportsTron ?? subscription.subscriptionPlan?.supportsTron) && (
                    <Tag color="red" style={{ padding: '4px 12px', borderRadius: '6px' }}>
                      TRON {subscription.customSupportsTron != null && '★'}
                    </Tag>
                  )}
                  {(subscription.customSupportsCustomChains ?? subscription.subscriptionPlan?.supportsCustomChains) && (
                    <Tag color="green" style={{ padding: '4px 12px', borderRadius: '6px' }}>
                      Custom Chains {subscription.customSupportsCustomChains != null && '★'}
                    </Tag>
                  )}
                  {!(subscription.customSupportsEvm ?? subscription.subscriptionPlan?.supportsEvm) && 
                   !(subscription.customSupportsBitcoin ?? subscription.subscriptionPlan?.supportsBitcoin) && 
                   !(subscription.customSupportsSolana ?? subscription.subscriptionPlan?.supportsSolana) && 
                   !(subscription.customSupportsTron ?? subscription.subscriptionPlan?.supportsTron) && 
                   !(subscription.customSupportsCustomChains ?? subscription.subscriptionPlan?.supportsCustomChains) && (
                    <span style={{ color: COLORS.textSecondary }}>No blockchain support configured</span>
                  )}
                </div>
                {subscription.subscriptionPlan?.name?.toUpperCase() === 'CUSTOM' && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: COLORS.textSecondary }}>
                    ★ = Custom override set for this merchant
                  </div>
                )}

                <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${COLORS.border}` }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: COLORS.textSecondary, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Billing Details
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Billing Date</span>
                      <span style={{ color: COLORS.textPrimary, fontSize: '13px' }}>Day {subscription.billingDate || 1} of month</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Next Billing</span>
                      <span style={{ color: COLORS.textPrimary, fontSize: '13px' }}>
                        {subscription.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString() : '-'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Auto Renew</span>
                      <Tag color={subscription.autoRenew ? 'green' : 'orange'} style={{ margin: 0 }}>
                        {subscription.autoRenew ? 'Yes' : 'No'}
                      </Tag>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '48px 24px',
              background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%)',
              borderRadius: '12px',
              border: `1px dashed ${COLORS.border}`,
            }}>
              <CrownOutlined style={{ fontSize: '48px', color: COLORS.textSecondary, marginBottom: '16px' }} />
              <div style={{ fontSize: '18px', fontWeight: 600, color: COLORS.textPrimary, marginBottom: '8px' }}>
                No Subscription Assigned
              </div>
              <p style={{ color: COLORS.textSecondary, marginBottom: '24px' }}>
                This merchant doesn't have a subscription plan yet. Assign a plan to enable billing and set resource limits.
              </p>
              <Button
                type="primary"
                icon={<CrownOutlined />}
                onClick={handleOpenSubscriptionModal}
                style={{ background: COLORS.accent, borderColor: COLORS.accent }}
              >
                Assign Subscription Plan
              </Button>
            </div>
          )}
        </Spin>
      </StyledCard>
      )}

      {/* Credits & Internal Wallet Section - Hidden for Default Merchant */}
      {!companyData.isDefault && (
      <StyledCard
        title={
          <Space>
            <WalletOutlined />
            <span>Credits & Internal Wallet</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              creditForm.resetFields();
              setAddCreditModalVisible(true);
            }}
            style={{ background: COLORS.accent, borderColor: COLORS.accent }}
          >
            Add Credit
          </Button>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Spin spinning={loadingCredits}>
              <div style={{
                background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%)',
                borderRadius: '12px',
                padding: '24px',
                border: `1px solid ${COLORS.border}`,
                height: '100%',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white',
                  }}>
                    <GiftOutlined />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: COLORS.textSecondary }}>Credit Balance</div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: '#10B981' }}>
                      ${parseFloat(creditBalance?.toString() || '0').toFixed(2)}
                    </div>
                  </div>
                </div>
                {creditTransactions.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginBottom: '8px', textTransform: 'uppercase' }}>
                      Recent Transactions
                    </div>
                    {creditTransactions.slice(0, 3).map((tx: any, idx: number) => (
                      <div key={tx.id || idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        borderBottom: `1px solid ${COLORS.border}`,
                        fontSize: '13px',
                      }}>
                        <span style={{ color: COLORS.textSecondary }}>{tx.description || tx.transactionType}</span>
                        <span style={{ color: tx.amount > 0 ? '#10B981' : '#EF4444', fontWeight: 600 }}>
                          {tx.amount > 0 ? '+' : ''}${parseFloat(tx.amount?.toString() || '0').toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Spin>
          </Col>
          <Col xs={24} md={12}>
            <Spin spinning={loadingWallet}>
              <div style={{
                background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%)',
                borderRadius: '12px',
                padding: '24px',
                border: `1px solid ${COLORS.border}`,
                height: '100%',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: 'white',
                  }}>
                    <WalletOutlined />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: COLORS.textSecondary }}>Internal Wallet Balance</div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: '#3B82F6' }}>
                      ${parseFloat(internalWallet?.balance?.toString() || '0').toFixed(2)}
                    </div>
                  </div>
                </div>
                {internalWallet?.depositAddress && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginBottom: '8px', textTransform: 'uppercase' }}>
                      Deposit Address
                    </div>
                    <div style={{
                      background: COLORS.bgPrimary,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      padding: '12px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      color: COLORS.accent,
                      wordBreak: 'break-all',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px',
                    }}>
                      <span>{internalWallet.depositAddress}</span>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(internalWallet.depositAddress);
                          message.success('Address copied!');
                        }}
                        style={{ color: COLORS.textSecondary }}
                      />
                    </div>
                  </div>
                )}
                {walletTransactions.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginBottom: '8px', textTransform: 'uppercase' }}>
                      Recent Transactions
                    </div>
                    {walletTransactions.slice(0, 3).map((tx: any, idx: number) => (
                      <div key={tx.id || idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        borderBottom: `1px solid ${COLORS.border}`,
                        fontSize: '13px',
                      }}>
                        <span style={{ color: COLORS.textSecondary }}>{tx.transactionType}</span>
                        <span style={{ color: tx.amount > 0 ? '#10B981' : '#EF4444', fontWeight: 600 }}>
                          {tx.amount > 0 ? '+' : ''}${parseFloat(tx.amount?.toString() || '0').toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {!internalWallet && (
                  <div style={{ textAlign: 'center', padding: '24px', color: COLORS.textSecondary }}>
                    No internal wallet created yet
                  </div>
                )}
              </div>
            </Spin>
          </Col>
        </Row>
      </StyledCard>
      )}

      {/* Resource Usage Section - Hidden for Default Merchant */}
      {!companyData.isDefault && subscription && (
        <StyledCard
          title={
            <Space>
              <HistoryOutlined />
              <span>Resource Usage</span>
            </Space>
          }
          extra={
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchResourceUsage}
              loading={loadingResourceUsage}
              style={{ background: COLORS.bgSecondary, borderColor: COLORS.border, color: COLORS.textPrimary }}
            >
              Refresh
            </Button>
          }
        >
          <Spin spinning={loadingResourceUsage}>
            <Row gutter={[16, 16]}>
              {[
                { key: 'WALLET', label: 'Wallets', limit: subscription.customWalletLimit ?? subscription.subscriptionPlan?.walletLimit, color: '#3B82F6' },
                { key: 'STAFF', label: 'Staff Members', limit: subscription.customStaffLimit ?? subscription.subscriptionPlan?.staffLimit, color: '#8B5CF6' },
                { key: 'BLOCKCHAIN', label: 'Blockchains', limit: subscription.customBlockchainLimit ?? subscription.subscriptionPlan?.blockchainLimit, color: '#F59E0B' },
                { key: 'CURRENCY', label: 'Currencies', limit: subscription.customCurrencyLimit ?? subscription.subscriptionPlan?.currencyLimit, color: '#10B981' },
                { key: 'CONTRACT', label: 'Contracts', limit: subscription.customContractLimit ?? subscription.subscriptionPlan?.contractLimit, color: '#EC4899' },
              ].map((resource) => {
                const usage = resourceUsage.find((r: any) => r.resourceType === resource.key);
                const current = usage?.currentCount || 0;
                const limit = resource.limit === -1 ? Infinity : (resource.limit || 0);
                const percentage = limit === Infinity ? 0 : Math.min(100, Math.round((current / limit) * 100));
                const isOverage = current > limit && limit !== Infinity;
                
                return (
                  <Col xs={24} sm={12} md={8} key={resource.key}>
                    <div style={{
                      background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%)',
                      borderRadius: '12px',
                      padding: '16px',
                      border: `1px solid ${isOverage ? '#EF4444' : COLORS.border}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>{resource.label}</span>
                        <span style={{ 
                          color: isOverage ? '#EF4444' : (percentage >= 80 ? '#F59E0B' : COLORS.textPrimary), 
                          fontWeight: 600, 
                          fontSize: '14px' 
                        }}>
                          {current} / {limit === Infinity ? '∞' : limit}
                        </span>
                      </div>
                      <Progress 
                        percent={percentage} 
                        strokeColor={isOverage ? '#EF4444' : (percentage >= 80 ? '#F59E0B' : resource.color)}
                        trailColor={COLORS.border}
                        showInfo={false}
                        size="small"
                      />
                      {isOverage && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#EF4444' }}>
                          <ExclamationCircleOutlined /> Overage: +{current - limit} (${usage?.overageCount * (usage?.overageUnitPrice || 0)}/mo)
                        </div>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Spin>
        </StyledCard>
      )}

      {/* Recent Invoices Section - Hidden for Default Merchant */}
      {!companyData.isDefault && (
      <StyledCard
        title={
          <Space>
            <FileTextOutlined />
            <span>Recent Invoices</span>
          </Space>
        }
        extra={
          <Button
            type="link"
            onClick={() => navigate(`/kivu/invoices?merchantId=${companyId}`)}
            style={{ color: COLORS.accent, padding: 0 }}
          >
            View All Invoices
          </Button>
        }
      >
        <Spin spinning={loadingInvoices}>
          {invoices.length > 0 ? (
            <Table
              dataSource={invoices}
              rowKey="id"
              pagination={false}
              style={{ background: 'transparent' }}
              columns={[
                {
                  title: 'Invoice #',
                  dataIndex: 'invoiceNumber',
                  key: 'invoiceNumber',
                  render: (text: string) => <span style={{ color: COLORS.accent, fontFamily: 'monospace' }}>{text}</span>,
                },
                {
                  title: 'Amount',
                  dataIndex: 'total',
                  key: 'total',
                  render: (total: number) => <span style={{ fontWeight: 600 }}>${parseFloat(total?.toString() || '0').toFixed(2)}</span>,
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status: string) => {
                    const colorMap: Record<string, string> = {
                      PAID: 'green',
                      PENDING: 'orange',
                      FAILED: 'red',
                      CANCELLED: 'default',
                      DRAFT: 'blue',
                    };
                    return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
                  },
                },
                {
                  title: 'Due Date',
                  dataIndex: 'dueDate',
                  key: 'dueDate',
                  render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
                },
                {
                  title: 'Paid',
                  dataIndex: 'paidAt',
                  key: 'paidAt',
                  render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
                },
              ]}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: COLORS.textSecondary }}>
              <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
              <div>No invoices found for this merchant</div>
            </div>
          )}
        </Spin>
      </StyledCard>
      )}

      {/* KYC / Business Verification Section */}
      <StyledCard
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SafetyCertificateOutlined style={{ color: COLORS.accent }} />
              <span>KYC / Business Verification</span>
            </div>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                kycForm.resetFields();
                setKycModalVisible(true);
              }}
              style={{
                background: COLORS.bgPrimary,
                borderColor: COLORS.border,
                color: COLORS.textPrimary,
              }}
            >
              Update KYB Status
            </Button>
          </div>
        }
      >
        <Spin spinning={loadingKyc}>
          {kycData ? (
            <>
              {/* Business Verification */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ color: COLORS.textSecondary, fontSize: 12, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Business Verification (KYB)
                </div>
                <Descriptions bordered size="small" column={2} style={{ background: 'transparent' }}>
                  <Descriptions.Item label="Status">
                    {(() => {
                      const s = kycData.merchantCompany?.businessVerificationStatus;
                      if (s === 'APPROVED') return <Tag icon={<CheckCircleOutlined />} color="success">Approved</Tag>;
                      if (s === 'REJECTED') return <Tag icon={<CloseCircleOutlined />} color="error">Rejected</Tag>;
                      if (s === 'SUBMITTED') return <Tag icon={<ExclamationCircleOutlined />} color="processing">Submitted</Tag>;
                      return <Tag icon={<ClockCircleOutlined />} color="default">Not Started</Tag>;
                    })()}
                  </Descriptions.Item>
                  {kycData.businessVerification?.verificationId && (
                    <Descriptions.Item label="Verification ID">
                      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {kycData.businessVerification.verificationId}
                      </span>
                    </Descriptions.Item>
                  )}
                  {kycData.businessVerification?.companyName && (
                    <Descriptions.Item label="Company Name">
                      {kycData.businessVerification.companyName}
                    </Descriptions.Item>
                  )}
                  {kycData.businessVerification?.companyNumber && (
                    <Descriptions.Item label="Company Number">
                      {kycData.businessVerification.companyNumber}
                    </Descriptions.Item>
                  )}
                  {kycData.businessVerification?.registrationCountry && (
                    <Descriptions.Item label="Country">
                      {kycData.businessVerification.registrationCountry}
                    </Descriptions.Item>
                  )}
                  {kycData.businessVerification?.contactEmail && (
                    <Descriptions.Item label="Contact Email">
                      {kycData.businessVerification.contactEmail}
                    </Descriptions.Item>
                  )}
                  {kycData.businessVerification?.rejectionReason && (
                    <Descriptions.Item label="Rejection Reason" span={2}>
                      <span style={{ color: '#ff4d4f' }}>{kycData.businessVerification.rejectionReason}</span>
                    </Descriptions.Item>
                  )}
                  {kycData.businessVerification?.createdAt && (
                    <Descriptions.Item label="Submitted At">
                      {new Date(kycData.businessVerification.createdAt).toLocaleString()}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>

              {/* Personal KYC records */}
              {kycData.standardKycRecords && kycData.standardKycRecords.length > 0 && (
                <div>
                  <div style={{ color: COLORS.textSecondary, fontSize: 12, fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Personal KYC Records
                  </div>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={kycData.standardKycRecords}
                    rowKey="id"
                    columns={[
                      {
                        title: 'Merchant',
                        key: 'merchant',
                        render: (_: any, record: any) => {
                          const merchant = kycData.merchants?.find((m: any) => m.id === record.merchantId);
                          return merchant ? `${merchant.firstName} ${merchant.lastName}` : record.merchantId;
                        },
                      },
                      {
                        title: 'Status',
                        dataIndex: 'verificationStatus',
                        key: 'verificationStatus',
                        render: (s: string) => {
                          if (s === 'APPROVED') return <Tag color="success">Approved</Tag>;
                          if (s === 'REJECTED') return <Tag color="error">Rejected</Tag>;
                          if (s === 'SUBMITTED') return <Tag color="processing">Submitted</Tag>;
                          return <Tag color="default">Pending</Tag>;
                        },
                      },
                      {
                        title: 'Submitted',
                        dataIndex: 'createdAt',
                        key: 'createdAt',
                        render: (d: string) => d ? new Date(d).toLocaleDateString() : '—',
                      },
                    ]}
                  />
                </div>
              )}
            </>
          ) : (
            <div style={{ color: COLORS.textSecondary, fontSize: 13, padding: '8px 0' }}>
              No KYC data available.
            </div>
          )}
        </Spin>
      </StyledCard>

      {companyData.merchants && companyData.merchants.length > 0 && (
        <StyledCard title="Associated Merchants">
          <Table
            columns={merchantColumns}
            dataSource={companyData.merchants}
            rowKey="id"
            pagination={false}
            style={{ background: 'transparent' }}
          />
        </StyledCard>
      )}

      <StyledCard title="Supported Resources">
        <Tabs
          defaultActiveKey="networks"
          items={[
            {
              key: 'networks',
              label: `Blockchain Networks (${supportedNetworks.filter((n: any) => n.isSupported).length}/${supportedNetworks.length})`,
              children: (
                <Spin spinning={loadingSupported}>
                  <Table
                    columns={supportedNetworksColumns}
                    dataSource={supportedNetworks.filter((n: any) => n.isSupported)}
                    rowKey="id"
                    pagination={false}
                    style={{ background: 'transparent' }}
                    locale={{ emptyText: 'No supported blockchain networks' }}
                  />
                </Spin>
              ),
            },
            {
              key: 'currencies',
              label: `Currencies (${supportedCurrencies.filter((c: any) => c.isSupported || c.supportedContracts > 0).length}/${supportedCurrencies.length})`,
              children: (
                <Spin spinning={loadingSupported}>
                  <Table
                    columns={supportedCurrenciesColumns}
                    dataSource={supportedCurrencies.filter((c: any) => c.isSupported || c.supportedContracts > 0)}
                    rowKey="id"
                    pagination={supportedCurrencies.length > 10 ? { pageSize: 10 } : false}
                    style={{ background: 'transparent' }}
                    locale={{ emptyText: 'No supported currencies' }}
                  />
                </Spin>
              ),
            },
            {
              key: 'contracts',
              label: `Contract Addresses (${supportedContracts.filter((c: any) => c.isSupported).length}/${supportedContracts.length})`,
              children: (
                <Spin spinning={loadingSupported}>
                  <Table
                    columns={supportedContractsColumns}
                    dataSource={supportedContracts.filter((c: any) => c.isSupported)}
                    rowKey="id"
                    pagination={false}
                    style={{ background: 'transparent' }}
                    locale={{ emptyText: 'No supported contract addresses' }}
                  />
                </Spin>
              ),
            },
          ]}
          style={{ color: COLORS.textPrimary }}
        />
      </StyledCard>

      <Modal
        title="Edit Company Status"
        open={editModalVisible}
        onOk={handleUpdateStatus}
        onCancel={() => setEditModalVisible(false)}
        okText="Update"
        cancelText="Cancel"
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="isDeleted"
            label={
              <Space>
                <span>Company Status</span>
                {companyData && (
                  <Tag color={companyData.status === 'DELETED' ? 'red' : 'green'}>
                    Current: {companyData.status}
                  </Tag>
                )}
              </Space>
            }
            valuePropName="checked"
            tooltip="Setting this to ON will mark the company as DELETED. This will also delete all associated merchant accounts and kill their active sessions. Setting it to OFF will reactivate the company (status = ACTIVE)."
          >
            <Switch
              checkedChildren="Deleted"
              unCheckedChildren="Active"
            />
          </Form.Item>

          <Form.Item
            name="isDisabled"
            label={
              <Space>
                <span>Merchant Status</span>
                {companyData && (
                  <Tag color={companyData.merchantStatus === 'SUSPENDED' ? 'orange' : companyData.merchantStatus === 'PENDING' ? 'gold' : 'green'}>
                    Current: {companyData.merchantStatus}
                  </Tag>
                )}
              </Space>
            }
            valuePropName="checked"
            tooltip={
              companyData?.merchantStatus === 'PENDING'
                ? "Merchant status is PENDING. Use the 'Activate Merchant' button to activate and assign a gas wallet."
                : "Setting this to ON will suspend the company (merchantStatus = SUSPENDED). The company will not be able to use the system until reactivated. Setting it to OFF will activate the company (merchantStatus = ACTIVE)."
            }
          >
            <Switch
              checkedChildren="Suspended"
              unCheckedChildren="Active"
              disabled={companyData?.merchantStatus === 'PENDING'}
            />
          </Form.Item>

          <div style={{ 
            marginTop: 24, 
            marginBottom: 16, 
            padding: 12, 
            background: `${COLORS.accent}10`, 
            borderRadius: 6,
            border: `1px solid ${COLORS.accent}40`
          }}>
            <div style={{ color: COLORS.accent, fontSize: '12px', fontWeight: 600, marginBottom: 8 }}>
              Sync Options
            </div>
            
            <Form.Item
              name="resetAsoRockAcknowledged"
              label={
                <Space>
                  <span>Reset Aso Rock Acknowledged</span>
                  {companyData && (
                    <Tag color={companyData.asorockAcknowledged ? 'green' : 'orange'}>
                      Current: {companyData.asorockAcknowledged ? 'Yes' : 'No'}
                    </Tag>
                  )}
                </Space>
              }
              valuePropName="checked"
              tooltip="Setting this to ON will reset the Aso Rock acknowledgment status to 'No'. This will trigger Aso Rock to re-sync this merchant company on the next sync cycle."
              style={{ marginBottom: 12 }}
            >
              <Switch
                checkedChildren="Reset"
                unCheckedChildren="Keep"
              />
            </Form.Item>

            <Form.Item
              name="resetWatcherId"
              label={
                <Space>
                  <span>Reset Watcher ID</span>
                  {companyData && (
                    <Tag color={companyData.watcherId ? 'green' : 'default'}>
                      Current: {companyData.watcherId ? 'Set' : 'Not Set'}
                    </Tag>
                  )}
                </Space>
              }
              valuePropName="checked"
              tooltip="Setting this to ON will clear the Watcher ID. This will allow the company to be re-registered with OnchainWatcher on the next sync."
              style={{ marginBottom: 0 }}
            >
              <Switch
                checkedChildren="Reset"
                unCheckedChildren="Keep"
                disabled={!companyData?.watcherId}
              />
            </Form.Item>
          </div>

          <div style={{ 
            marginTop: 16, 
            padding: 12, 
            background: COLORS.bgPrimary, 
            borderRadius: 6,
            border: `1px solid ${COLORS.border}`
          }}>
            <div style={{ color: COLORS.textSecondary, fontSize: '12px', lineHeight: '1.6' }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Status Differences:</strong>
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>• Merchant Status (SUSPENDED):</strong> Merchants can still login, but the company will not receive ready wallets or callbacks. The company is temporarily disabled.
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>• Company Status (DELETED):</strong> Merchants <strong>cannot login</strong>. All associated merchant accounts are also deleted and their active sessions are terminated. This is a permanent action.
              </div>
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${COLORS.border}` }}>
                <strong>Note:</strong> You can toggle these switches on/off to change the company status. Changes take effect immediately.
              </div>
            </div>
          </div>
        </Form>
      </Modal>

      {/* KYB Update Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SafetyCertificateOutlined style={{ color: COLORS.accent }} />
            <span>Update Business Verification (KYB)</span>
          </div>
        }
        open={kycModalVisible}
        onOk={handleUpdateKyc}
        onCancel={() => { setKycModalVisible(false); kycForm.resetFields(); }}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={updatingKyc}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
        width={520}
      >
        <div style={{ marginBottom: 16, padding: 12, background: COLORS.bgPrimary, borderRadius: 6, border: `1px solid ${COLORS.border}` }}>
          <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>
            Current status:{' '}
            <strong style={{ color: COLORS.textPrimary }}>
              {kycData?.merchantCompany?.businessVerificationStatus || 'PENDING'}
            </strong>
          </div>
        </div>
        <Form form={kycForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="kycAction"
            label="Action"
            rules={[{ required: true, message: 'Please select an action' }]}
          >
            <Select
              placeholder="Select action"
              style={{ width: '100%' }}
              options={[
                { value: 'APPROVE', label: 'Approve — mark business verification as APPROVED' },
                { value: 'REJECT', label: 'Reject — mark business verification as REJECTED' },
              ]}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.kycAction !== curr.kycAction}
          >
            {({ getFieldValue }) =>
              getFieldValue('kycAction') === 'REJECT' ? (
                <Form.Item
                  name="rejectionReason"
                  label="Rejection Reason"
                  rules={[{ required: true, message: 'Please provide a rejection reason' }]}
                >
                  <Input.TextArea
                    rows={3}
                    placeholder="Explain why the business verification is being rejected..."
                    style={{ background: COLORS.bgPrimary, borderColor: COLORS.border, color: COLORS.textPrimary }}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>

      {/* Activate Merchant Modal */}
      <Modal
        title="Activate Merchant"
        open={activateModalVisible}
        onOk={handleActivateMerchant}
        onCancel={() => setActivateModalVisible(false)}
        okText="Activate"
        cancelText="Cancel"
        confirmLoading={activating}
        okButtonProps={{
          disabled: !companyData?.custodialEVMAddress,
          style: { background: COLORS.accent, borderColor: COLORS.accent }
        }}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <div style={{ marginBottom: 16 }}>
          {!companyData?.custodialEVMAddress ? (
            <div style={{ 
              padding: 12, 
              background: '#FEF3C7', 
              borderRadius: 6,
              border: '1px solid #FCD34D',
              color: '#92400E',
              marginBottom: 16
            }}>
              <Space>
                <InfoCircleOutlined />
                <strong>EVM Custodial Wallet Required</strong>
              </Space>
              <div style={{ marginTop: 8, fontSize: '13px' }}>
                Merchant must add a custodial EVM wallet before activation.
              </div>
            </div>
          ) : (
            <div style={{ 
              padding: 12, 
              background: '#D1FAE5', 
              borderRadius: 6,
              border: '1px solid #6EE7B7',
              color: '#065F46',
              marginBottom: 16
            }}>
              <Space>
                <CheckCircleOutlined />
                <strong>EVM Custodial Wallet Configured</strong>
              </Space>
              <div style={{ marginTop: 8, fontSize: '13px' }}>
                {companyData.custodialEVMAddress}
              </div>
            </div>
          )}
        </div>

        <div style={{ 
          padding: 12, 
          background: COLORS.bgPrimary, 
          borderRadius: 6,
          border: `1px solid ${COLORS.border}`,
          marginBottom: 16
        }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '13px', lineHeight: '1.6' }}>
            <div style={{ marginBottom: 8 }}>
              <strong>What happens when you activate:</strong>
            </div>
            <div style={{ marginBottom: 4 }}>
              • Merchant status will change from PENDING to ACTIVE
            </div>
            <div style={{ marginBottom: 4 }}>
              • A gas wallet will be assigned to this merchant
            </div>
            <div style={{ marginBottom: 4 }}>
              • The gas wallet will be borrowed from the default merchant if needed
            </div>
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${COLORS.border}` }}>
              <Space>
                <InfoCircleOutlined />
                <span>
                  <strong>Gas Wallet:</strong> Used to fund child wallets for EVM and Solana sweeps.
                </span>
              </Space>
            </div>
          </div>
        </div>
      </Modal>

      {/* Public Key Modal */}
      <Modal
        title="RSA Public Key"
        open={publicKeyModalVisible}
        onCancel={() => setPublicKeyModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPublicKeyModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        {companyData?.publicKey && (
          <div>
            <div style={{ marginBottom: 16, color: COLORS.textSecondary, fontSize: '14px' }}>
              This is the RSA public key used for API request signature verification. Merchants use their private key to sign API requests, and this public key is used by the system to verify those signatures.
            </div>
            <div style={{
              background: COLORS.bgPrimary,
              padding: '16px',
              borderRadius: '6px',
              border: `1px solid ${COLORS.border}`,
              fontFamily: 'monospace',
              fontSize: '12px',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
              maxHeight: '400px',
              overflow: 'auto',
              color: COLORS.textPrimary,
            }}>
              {companyData.publicKey}
            </div>
            <div style={{ marginTop: 16 }}>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(companyData.publicKey);
                  message.success('Public key copied to clipboard');
                }}
                style={{ background: COLORS.bgPrimary, borderColor: COLORS.border, color: COLORS.textPrimary }}
              >
                Copy to Clipboard
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Subscription Modal */}
      <Modal
        title={
          <Space>
            <CrownOutlined style={{ color: COLORS.accent }} />
            <span>{subscription ? 'Change Subscription Plan' : 'Assign Subscription Plan'}</span>
          </Space>
        }
        open={subscriptionModalVisible}
        onCancel={() => {
          setSubscriptionModalVisible(false);
          setSelectedPlanId(null);
        }}
        onOk={handleAssignSubscription}
        okText={subscription ? 'Update Plan' : 'Assign Plan'}
        confirmLoading={assigningPlan}
        okButtonProps={{ style: { background: COLORS.accent, borderColor: COLORS.accent } }}
        width={700}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form form={subscriptionForm} layout="vertical">
          <Form.Item
            name="subscriptionPlanId"
            label={<span style={{ color: COLORS.textPrimary }}>Select Subscription Plan</span>}
            rules={[{ required: true, message: 'Please select a subscription plan' }]}
          >
            <Input type="hidden" />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '-16px' }}>
            {subscriptionPlans.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: COLORS.textSecondary }}>
                <Spin size="small" /> Loading subscription plans...
              </div>
            ) : subscriptionPlans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => {
                  setSelectedPlanId(plan.id);
                  subscriptionForm.setFieldsValue({ subscriptionPlanId: plan.id });
                }}
                style={{
                  cursor: 'pointer',
                  padding: '16px',
                  borderRadius: '10px',
                  border: `2px solid ${selectedPlanId === plan.id ? COLORS.accent : COLORS.border}`,
                  background: selectedPlanId === plan.id 
                    ? 'rgba(34, 211, 238, 0.15)' 
                    : COLORS.bgPrimary,
                  transition: 'all 0.2s ease',
                  transform: selectedPlanId === plan.id ? 'scale(1.02)' : 'scale(1)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: plan.name?.toUpperCase().includes('ENTERPRISE') || plan.name?.toUpperCase().includes('CUSTOM')
                      ? 'linear-gradient(135deg, #A371F7 0%, #8957E5 100%)'
                      : plan.name?.toUpperCase().includes('PRO')
                      ? 'linear-gradient(135deg, #58A6FF 0%, #1F6FEB 100%)'
                      : 'linear-gradient(135deg, #238636 0%, #2EA043 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '16px',
                  }}>
                    {plan.name?.toUpperCase().includes('ENTERPRISE') || plan.name?.toUpperCase().includes('CUSTOM') ? <CrownOutlined /> :
                     plan.name?.toUpperCase().includes('PRO') ? <ThunderboltOutlined /> :
                     <RocketOutlined />}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: COLORS.textPrimary, fontSize: '14px' }}>
                      {plan.displayName || plan.name}
                    </div>
                  </div>
                </div>
                
                {(() => {
                  // For the merchant's currently assigned plan, prefer their custom
                  // overrides over the shared plan template (relevant for CUSTOM plans,
                  // whose template defaults to 0/$0 until customized per-merchant).
                  const isCurrentPlan = subscription?.subscriptionPlanId === plan.id;
                  const displayPrice = isCurrentPlan
                    ? (subscription.effectivePrice ?? subscription.customMonthlyPrice ?? plan.monthlyPrice)
                    : plan.monthlyPrice;
                  const displayWalletLimit = isCurrentPlan
                    ? (subscription.customWalletLimit ?? plan.walletLimit)
                    : plan.walletLimit;
                  const displayStaffLimit = isCurrentPlan
                    ? (subscription.customStaffLimit ?? plan.staffLimit)
                    : plan.staffLimit;

                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '24px', fontWeight: 700, color: COLORS.textPrimary }}>
                          ${parseFloat(displayPrice || 0).toFixed(0)}
                        </span>
                        <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>/mo</span>
                      </div>

                      <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>
                        <div>Wallets: {displayWalletLimit === -1 ? '∞' : displayWalletLimit}</div>
                        <div>Staff: {displayStaffLimit === -1 ? '∞' : displayStaffLimit}</div>
                      </div>
                    </>
                  );
                })()}

                {selectedPlanId === plan.id && (
                  <div style={{ 
                    marginTop: '8px', 
                    paddingTop: '8px', 
                    borderTop: `1px solid ${COLORS.border}`,
                    textAlign: 'center',
                  }}>
                    <Tag color="cyan" icon={<CheckCircleOutlined />}>Selected</Tag>
                  </div>
                )}
              </div>
            ))}
          </div>

          {subscription && (
            <div style={{ 
              padding: '12px', 
              background: COLORS.bgPrimary, 
              borderRadius: '8px',
              border: `1px solid ${COLORS.border}`,
              marginTop: '16px',
            }}>
              <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                <strong>Current Plan:</strong> {subscription.subscriptionPlan?.displayName || subscription.subscriptionPlan?.name}
                <br />
                <strong>Status:</strong> <Tag color={subscription.status === 'ACTIVE' ? 'green' : 'orange'}>{subscription.status}</Tag>
              </div>
            </div>
          )}
        </Form>
      </Modal>

      {/* Add Credit Modal */}
      <Modal
        title={
          <Space>
            <GiftOutlined style={{ color: '#10B981' }} />
            <span>Add Credit to Merchant</span>
          </Space>
        }
        open={addCreditModalVisible}
        onCancel={() => {
          setAddCreditModalVisible(false);
          creditForm.resetFields();
        }}
        onOk={handleAddCredit}
        okText="Add Credit"
        confirmLoading={addingCredit}
        okButtonProps={{ style: { background: '#10B981', borderColor: '#10B981' } }}
        width={500}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        <Form form={creditForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="amount"
            label="Credit Amount (USD)"
            rules={[
              { required: true, message: 'Please enter credit amount' },
              { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
            ]}
          >
            <InputNumber
              prefix="$"
              min={0.01}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              placeholder="Enter amount"
            />
          </Form.Item>

          <Form.Item
            name="creditType"
            label="Credit Type"
            initialValue="ADJUSTMENT"
          >
            <Select>
              <Select.Option value="PROMOTIONAL">Promotional</Select.Option>
              <Select.Option value="REFUND">Refund</Select.Option>
              <Select.Option value="ADJUSTMENT">Adjustment</Select.Option>
              <Select.Option value="PAID">Paid Purchase</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description / Reason"
            rules={[{ required: true, message: 'Please provide a description' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter reason for adding credit..."
            />
          </Form.Item>

          <div style={{
            background: COLORS.bgPrimary,
            padding: '12px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>
              Current Credit Balance: <strong style={{ color: '#10B981' }}>${parseFloat(creditBalance?.toString() || '0').toFixed(2)}</strong>
            </div>
          </div>
        </Form>
      </Modal>

      {/* Custom Pricing & Limits Modal */}
      <Modal
        title={
          <Space>
            <PercentageOutlined style={{ color: COLORS.accent }} />
            <span>Custom Pricing & Limits</span>
            {subscription?.subscriptionPlan?.name?.toUpperCase() === 'CUSTOM' && (
              <Tag color="purple">CUSTOM PLAN</Tag>
            )}
          </Space>
        }
        open={pricingModalVisible}
        onCancel={() => {
          setPricingModalVisible(false);
          pricingForm.resetFields();
        }}
        onOk={handleUpdatePricing}
        okText="Update Settings"
        confirmLoading={updatingPricing}
        okButtonProps={{ style: { background: COLORS.accent, borderColor: COLORS.accent } }}
        width={800}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Form form={pricingForm} layout="vertical" style={{ marginTop: 16 }}>
          {/* Pricing Section */}
          <div style={{
            background: COLORS.bgPrimary,
            padding: '16px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            marginBottom: '16px',
          }}>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>Plan Base Price</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: COLORS.textPrimary }}>
                  ${parseFloat(subscription?.subscriptionPlan?.monthlyPrice || 0).toFixed(2)}/mo
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>Current Effective Price</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: COLORS.accent }}>
                  ${parseFloat(subscription?.effectivePrice || subscription?.subscriptionPlan?.monthlyPrice || 0).toFixed(2)}/mo
                </div>
              </Col>
            </Row>
          </div>

          <Form.Item
            name="customMonthlyPrice"
            label="Custom Monthly Price (leave empty to use plan price)"
          >
            <InputNumber
              prefix="$"
              min={0}
              step={1}
              precision={2}
              style={{ width: '100%' }}
              placeholder="Enter custom price or leave empty"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="discountType"
                label="Discount Type"
              >
                <Select allowClear placeholder="Select discount type">
                  <Select.Option value="PERCENTAGE">Percentage (%)</Select.Option>
                  <Select.Option value="FIXED_AMOUNT">Fixed Amount ($)</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="discountValue"
                label="Discount Value"
              >
                <InputNumber
                  min={0}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="Enter value"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Custom Resource Limits Section - Only show for CUSTOM plan */}
          {subscription?.subscriptionPlan?.name?.toUpperCase() === 'CUSTOM' && (
            <>
              <div style={{
                borderTop: `1px solid ${COLORS.border}`,
                margin: '24px 0 16px 0',
                paddingTop: '16px',
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: COLORS.textPrimary, marginBottom: '16px' }}>
                  <CrownOutlined style={{ marginRight: 8, color: '#A371F7' }} />
                  Custom Resource Limits
                  <span style={{ fontSize: '12px', fontWeight: 400, color: COLORS.textSecondary, marginLeft: 8 }}>
                    (Use -1 for unlimited, leave empty to use defaults)
                  </span>
                </div>
              </div>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="customWalletLimit" label="Wallets Limit">
                    <InputNumber min={-1} style={{ width: '100%' }} placeholder="e.g., 5000 or -1" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="customStaffLimit" label="Staff Limit">
                    <InputNumber min={-1} style={{ width: '100%' }} placeholder="e.g., 50 or -1" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="customBlockchainLimit" label="Blockchains Limit">
                    <InputNumber min={-1} style={{ width: '100%' }} placeholder="e.g., 10 or -1" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="customCurrencyLimit" label="Currencies Limit">
                    <InputNumber min={-1} style={{ width: '100%' }} placeholder="e.g., 50 or -1" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="customContractLimit" label="Contracts Limit">
                    <InputNumber min={-1} style={{ width: '100%' }} placeholder="e.g., 200 or -1" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="customNativeTokenLimit" label="Native Tokens Limit">
                    <InputNumber min={-1} style={{ width: '100%' }} placeholder="e.g., 20 or -1" />
                  </Form.Item>
                </Col>
              </Row>

              <div style={{
                borderTop: `1px solid ${COLORS.border}`,
                margin: '16px 0',
                paddingTop: '16px',
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: COLORS.textPrimary, marginBottom: '16px' }}>
                  <ThunderboltOutlined style={{ marginRight: 8, color: '#F59E0B' }} />
                  Custom Blockchain Support
                </div>
              </div>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="customSupportsEvm" label="EVM Chains" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="customSupportsBitcoin" label="Bitcoin" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="customSupportsSolana" label="Solana" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="customSupportsTron" label="TRON" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="customSupportsCustomChains" label="Custom Chains" valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Form.Item
            name="pricingNotes"
            label="Pricing Notes (internal use)"
          >
            <Input.TextArea
              rows={2}
              placeholder="e.g., Enterprise deal - 20% partner discount, custom limits for 5000 wallets"
            />
          </Form.Item>

          <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid rgba(245, 158, 11, 0.3)',
          }}>
            <div style={{ fontSize: '13px', color: '#F59E0B' }}>
              <ExclamationCircleOutlined style={{ marginRight: 8 }} />
              {subscription?.subscriptionPlan?.name?.toUpperCase() === 'CUSTOM' 
                ? 'All custom pricing and limits will take effect immediately.'
                : 'Pricing changes will take effect immediately. Historical invoices will not be affected.'}
            </div>
          </div>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default CompanyDetails;

